// Back / forward for the WEB app (the Mac needs none; a browser tab does): the same history as the
// browser's own buttons, shown in every main-column header, disabled when there is nowhere to go.
// Alt+←/→ and ⌘[ / ⌘] walk it from the keyboard.
import { useEffect, useState } from 'react';
import { Icon } from '../../design/icons';
import { t } from '../../i18n/i18n';
import { goBack, goForward, historyPosition, onHistoryChange } from '../router';

export function useHistoryPosition() {
  const [pos, setPos] = useState(historyPosition);
  useEffect(() => onHistoryChange(() => setPos(historyPosition())), []);
  return pos;
}

export function HistoryArrows() {
  const { canGoBack, canGoForward } = useHistoryPosition();
  return (
    <div className="history-arrows">
      <button className="icon-button" onClick={goBack} disabled={!canGoBack} title={`${t('common.back')} (Alt+←)`} aria-label={t('common.back')}>
        <Icon name="chevronLeft" size={18} strokeWidth={2} />
      </button>
      <button className="icon-button" onClick={goForward} disabled={!canGoForward} title={`${t('web.nav.forward')} (Alt+→)`} aria-label={t('web.nav.forward')}>
        <Icon name="chevronRight" size={18} strokeWidth={2} />
      </button>
    </div>
  );
}

/** Keyboard history: Alt+←/→ everywhere, ⌘[ / ⌘] on the Mac — never while typing a word. */
export function useHistoryKeys(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest('input, textarea, [contenteditable]');
      if (e.altKey && !e.metaKey && !e.ctrlKey && !typing) {
        if (e.key === 'ArrowLeft') (e.preventDefault(), goBack());
        else if (e.key === 'ArrowRight') (e.preventDefault(), goForward());
      }
      if (e.metaKey && !e.altKey && (e.key === '[' || e.key === ']')) {
        e.preventDefault();
        if (e.key === '[') goBack();
        else goForward();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
